import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ConfirmModal from '../UI/ConfirmModal';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';

export default function TableGrid({ showNotification }) {
  const { t } = useLang();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTable, setNewTable] = useState({ tableNumber: '', capacity: 4 });
  const [addLoading, setAddLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, tableId: null, label: '' });

  // Table QR codes (扫码点餐)
  const [qrModal, setQrModal] = useState({ open: false, tableNumber: null, dataUri: '', loading: false });
  const [qrBusy, setQrBusy] = useState(false);

  const token = localStorage.getItem('ownerToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTables = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/tables`, { headers });
      if (res.data.success) setTables(res.data.data);
    } catch {
      showNotification('Failed to load tables', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTables(); }, []); // eslint-disable-line

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTable.tableNumber) return showNotification('Table number is required', 'error');
    setAddLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/tables`, newTable, { headers });
      if (res.data.success) {
        showNotification(`Table ${newTable.tableNumber} added!`, 'success');
        setNewTable({ tableNumber: '', capacity: 4 });
        setShowAddForm(false);
        fetchTables();
      } else {
        showNotification(res.data.message || 'Failed to add table', 'error');
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to add table', 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async () => {
    const { tableId } = confirmModal;
    setConfirmModal({ open: false, tableId: null, label: '' });
    try {
      const res = await axios.delete(`${API_BASE_URL}/tables/${tableId}`, { headers });
      if (res.data.success) {
        setTables(prev => prev.filter(t => t._id !== tableId));
        showNotification('Table removed', 'success');
      }
    } catch {
      showNotification('Failed to delete table', 'error');
    }
  };

  const handleMarkFree = async (tableId, tableNumber) => {
    try {
      const res = await axios.patch(`${API_BASE_URL}/tables/${tableId}/free`, {}, { headers });
      if (res.data.success) {
        setTables(prev => prev.map(t => t._id === tableId ? { ...t, isOccupied: false, currentOrderId: null } : t));
        showNotification(`Table ${tableNumber} marked as free`, 'success');
      }
    } catch {
      showNotification('Failed to update table', 'error');
    }
  };

  // ---- Table QR codes ------------------------------------------------------
  const closeQr = () => setQrModal({ open: false, tableNumber: null, dataUri: '', loading: false });

  const openQr = async (table) => {
    setQrModal({ open: true, tableNumber: table.tableNumber, dataUri: '', loading: true });
    try {
      const res = await axios.get(`${API_BASE_URL}/tables/${table._id}/qrcode`, { headers });
      if (res.data.success) {
        setQrModal({ open: true, tableNumber: table.tableNumber, dataUri: res.data.data.dataUri, loading: false });
      } else {
        closeQr();
        showNotification(res.data.message || 'Failed to generate QR code', 'error');
      }
    } catch (err) {
      closeQr();
      showNotification(err.response?.data?.message || 'Failed to generate QR code', 'error');
    }
  };

  const downloadQr = () => {
    if (!qrModal.dataUri) return;
    const a = document.createElement('a');
    a.href = qrModal.dataUri;
    a.download = `dewan-e-khass-table-${qrModal.tableNumber}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Build a printable sheet of table cards and send it to the printer.
  const printQrCards = (cards) => {
    const cardsHtml = cards.map(c => `
      <div class="qr-card">
        <div class="qr-brand">Dewan-e-Khass</div>
        <div class="qr-table">TABLE ${c.tableNumber}</div>
        <img src="${c.dataUri}" alt="Table ${c.tableNumber}" />
        <div class="qr-cta">扫码点餐</div>
        <div class="qr-sub">Scan to view the menu &amp; order</div>
      </div>`).join('');

    const html = `<!DOCTYPE html><html><head><title>Table QR Codes</title><style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',Arial,sans-serif;padding:8mm;background:#fff}
      .sheet{display:flex;flex-wrap:wrap;gap:8mm}
      .qr-card{width:85mm;border:2px solid #8B4513;border-radius:6mm;padding:6mm 4mm;text-align:center;page-break-inside:avoid}
      .qr-brand{font-size:13pt;font-weight:800;color:#8B4513;letter-spacing:.5px}
      .qr-table{font-size:22pt;font-weight:800;color:#3d1f0a;margin:2mm 0 3mm}
      .qr-card img{width:52mm;height:52mm;display:block;margin:0 auto}
      .qr-cta{font-size:14pt;font-weight:700;color:#8B4513;margin-top:3mm}
      .qr-sub{font-size:9pt;color:#777;margin-top:1mm}
      @media print{@page{margin:8mm}}
    </style></head><body><div class="sheet">${cardsHtml}</div></body></html>`;

    const existing = document.getElementById('qr-print-frame');
    if (existing) existing.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'qr-print-frame';
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 500);
  };

  const printAllQrs = async () => {
    setQrBusy(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/tables/qrcodes/all`, { headers });
      if (res.data.success && res.data.data.length > 0) {
        printQrCards(res.data.data);
        if (res.data.failed && res.data.failed.length > 0) {
          showNotification(`${res.data.failed.length} table(s) failed to generate`, 'error');
        }
      } else {
        showNotification(res.data.message || 'No QR codes generated', 'error');
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to generate QR codes', 'error');
    } finally {
      setQrBusy(false);
    }
  };

  const occupied = tables.filter(t => t.isOccupied).length;
  const free = tables.filter(t => !t.isOccupied).length;

  return (
    <div className="tables-page">
      <ConfirmModal
        isOpen={confirmModal.open}
        title={t('tables.removeTable')}
        message={`${t('common.remove')} ${confirmModal.label}?`}
        confirmText={t('common.remove')}
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmModal({ open: false, tableId: null, label: '' })}
      />

      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-layout-wtf me-2"></i>{t('tables.title')}</h2>
          <p className="page-subtitle">
            <span className="badge-occupied me-3"><i className="bi bi-circle-fill me-1"></i>{occupied} {t('tables.occupied')}</span>
            <span className="badge-free"><i className="bi bi-circle-fill me-1"></i>{free} {t('tables.free')}</span>
          </p>
        </div>
        <div className="page-header-actions">
          <button onClick={fetchTables} className="btn-secondary-action">
            <i className="bi bi-arrow-clockwise me-1"></i>{t('common.refresh')}
          </button>
          <button onClick={printAllQrs} className="btn-secondary-action" disabled={qrBusy || tables.length === 0}>
            {qrBusy
              ? <><span className="spinner-border spinner-border-sm me-1"></span>{t('tables.qrGenerating')}</>
              : <><i className="bi bi-qr-code me-1"></i>{t('tables.printAllQr')}</>}
          </button>
          <button onClick={() => setShowAddForm(v => !v)} className="btn-primary-action">
            <i className="bi bi-plus-circle me-2"></i>{t('tables.addTable')}
          </button>
        </div>
      </div>

      {/* Add Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.form
            className="add-table-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleAdd}
          >
            <div className="add-table-fields">
              <div className="form-group">
                <label className="form-label">{t('tables.tableNumber')} <span className="required">*</span></label>
                <input
                  type="number" min="1"
                  className="form-input"
                  placeholder="e.g. 5"
                  value={newTable.tableNumber}
                  onChange={e => setNewTable(p => ({ ...p, tableNumber: parseInt(e.target.value) }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('tables.capacity')}</label>
                <input
                  type="number" min="1" max="20"
                  className="form-input"
                  value={newTable.capacity}
                  onChange={e => setNewTable(p => ({ ...p, capacity: parseInt(e.target.value) }))}
                />
              </div>
              <div className="add-table-btns">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-cancel-form">{t('common.cancel')}</button>
                <button type="submit" className="btn-submit-form" disabled={addLoading}>
                  {addLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-plus-circle me-2"></i>}
                  {t('tables.addTable')}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner-border" style={{ color: '#a05a2c' }}></div>
          <p className="loading-text">{t('tables.loadingTables')}</p>
        </div>
      ) : tables.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-layout-wtf"></i>
          <p>{t('tables.noTables')}</p>
        </div>
      ) : (
        <motion.div className="table-grid" layout>
          <AnimatePresence>
            {tables
              .sort((a, b) => a.tableNumber - b.tableNumber)
              .map((table, i) => (
                <motion.div
                  key={table._id}
                  className={`table-card ${table.isOccupied ? 'occupied' : 'free'}`}
                  layout
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ delay: i * 0.04 }}
                  whileHover={{ y: -4, boxShadow: '0 12px 30px rgba(0,0,0,0.15)' }}
                >
                  {/* Status dot */}
                  <div className={`table-status-dot ${table.isOccupied ? 'occupied' : 'free'}`}></div>

                  {/* Table icon */}
                  <div className="table-icon">
                    <i className="bi bi-layout-wtf"></i>
                  </div>

                  <h4 className="table-number">{t('tables.table')} {table.tableNumber}</h4>
                  <p className="table-capacity">
                    <i className="bi bi-people me-1"></i>{table.capacity} {t('tables.seats')}
                  </p>
                  <span className={`table-badge ${table.isOccupied ? 'occupied' : 'free'}`}>
                    {table.isOccupied ? t('tables.occupied') : t('tables.free')}
                  </span>

                  <div className="table-actions">
                    {table.isOccupied && (
                      <button
                        className="btn-mark-free"
                        onClick={() => handleMarkFree(table._id, table.tableNumber)}
                        title={t('tables.markFree')}
                      >
                        <i className="bi bi-check-circle me-1"></i>{t('tables.markFree')}
                      </button>
                    )}
                    <button
                      className="btn-qr-icon"
                      onClick={() => openQr(table)}
                      title={t('tables.tableQr')}
                    >
                      <i className="bi bi-qr-code"></i>
                    </button>
                    <button
                      className="btn-delete-icon"
                      onClick={() => setConfirmModal({ open: true, tableId: table._id, label: `Table ${table.tableNumber}` })}
                      title="Remove table"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </motion.div>
              ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* QR code modal */}
      <AnimatePresence>
        {qrModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeQr}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, padding: 26, width: '100%', maxWidth: 380, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            >
              <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 700, color: '#3d1f0a' }}>
                {t('tables.table')} {qrModal.tableNumber}
              </h3>
              <p style={{ margin: '0 0 18px', fontSize: 12.5, color: '#9B8579', lineHeight: 1.5 }}>
                {t('tables.qrHint')}
              </p>

              {qrModal.loading ? (
                <div style={{ padding: '70px 0' }}>
                  <div className="spinner-border" style={{ color: '#a05a2c' }}></div>
                </div>
              ) : (
                <img
                  src={qrModal.dataUri}
                  alt={`Table ${qrModal.tableNumber}`}
                  style={{ width: 230, height: 230, display: 'block', margin: '0 auto', border: '1px solid #f0e8df', borderRadius: 12 }}
                />
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary-action" style={{ flex: 1 }} onClick={closeQr}>
                  {t('common.close')}
                </button>
                <button
                  className="btn-secondary-action"
                  style={{ flex: 1 }}
                  disabled={!qrModal.dataUri}
                  onClick={() => printQrCards([{ tableNumber: qrModal.tableNumber, dataUri: qrModal.dataUri }])}
                >
                  <i className="bi bi-printer me-1"></i>{t('common.print')}
                </button>
                <button className="btn-primary-action" style={{ flex: 1 }} disabled={!qrModal.dataUri} onClick={downloadQr}>
                  <i className="bi bi-download me-1"></i>{t('tables.download')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
